import { useState, useRef, useEffect, useContext } from 'react';
import { DataProvider } from '../../store/ContextProvider';

const PROGRESS_STYLES = {
    container: 'w-full bg-secondary-400 rounded-full h-2 overflow-hidden',
    bar: 'h-full bg-primary-400 transition-all duration-200 ease-out'
};

export default function ImportCandidate() {
    const { BeURL } = useContext(DataProvider);
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState({ processed: 0, total: 0, saved: 0 });
    const [status, setStatus] = useState('idle');
    const [logs, setLogs] = useState([]);
    const eventSourceRef = useRef(null);

    const logRef = useRef(null)

    const handleFileChange = e => {
        setFile(e.target.files[0]);
        setStatus('idle');
        setProgress({ processed: 0, total: 0, saved: 0 });
        setLogs([]);
    };

    const handleUpload = () => {
        if (!file || status === 'uploading') return;
        setStatus('uploading');

        const formData = new FormData();
        formData.append('file', file);

        if (eventSourceRef.current) eventSourceRef.current.close();
        const es = new EventSource(`${BeURL}/import-candidate-sse`, {withCredentials: true});
        eventSourceRef.current = es;

        es.onmessage = e => {
            if (!e.data || e.data === 'undefined') return;
            try {
                const data = JSON.parse(e.data);
                setProgress(data);
                setLogs(l => [...l, `Processed ${data.processed}/${data.total}, Saved: ${data.saved}`]);
            } catch {
                console.warn('Invalid SSE data:', e.data);
            }
        };

        es.addEventListener('done', () => { setStatus('done'); es.close(); });
        es.addEventListener('error', e => {
            if (e.data) {
                try { setLogs(l => [...l, `Error: ${JSON.parse(e.data).message}`]); setStatus('error'); }
                catch { console.error('SSE error', e); }
            }
            es.close();
        });

        fetch(`${BeURL}/import-candidate`, { method: 'POST', credentials: "include", body: formData })
            .then(async res => {
                if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
            })
            .catch(err => { setStatus('error'); setLogs(l => [...l, err.message]); if (eventSourceRef.current) eventSourceRef.current.close(); });
    };

    useEffect(() => {
        if (logRef.current) {
            logRef.current.scrollTop = logRef.current.scrollHeight;
        }
    }, [logs]);

    useEffect(() => () => { if (eventSourceRef.current) eventSourceRef.current.close(); }, []);


    return (
        <div className="min-h-[80vh] p-8 bg-gray-50 flex flex-col items-center">
            <h1 className="text-3xl font-semibold text-primary-400 mb-6">Import Candidates</h1>
            <div className="flex flex-wrap gap-1 justify-center items-center space-x-4 mb-6">
                <input
                    type="file"
                    accept=".xls,.xlsx"
                    onChange={handleFileChange}
                    className="border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400 w-100 file:bg-secondary-100 file:border-0 file:text-secondary-700 file:rounded-lg file:px-3 file:font-semibold"
                />
                {
                    status !== 'uploading' ?
                        <button
                            onClick={handleUpload}
                            disabled={!file || status === 'uploading'}
                            className={`px-6 py-2 rounded-xl text-white font-medium transition-colors
                            ${status === 'uploading' ? 'bg-secondary-400 cursor-not-allowed' : 'bg-primary-400 hover:bg-primary-500'}`}
                        >
                            {status === 'uploading' ? 'Uploading...' : 'Upload'}
                        </button>
                        : <button className='px-6 py-2 rounded-xl text-white font-medium transition-colors bg-red-500 hover:bg-red-700' onClick={() => window.location.reload()}>Stop</button>
                }

            </div>

            {status !== 'idle' && (
                <div className="w-full max-w-lg mb-6">
                    <div className={PROGRESS_STYLES.container}>
                        <div
                            className={PROGRESS_STYLES.bar}
                            style={{ width: progress.total ? `${(progress.processed / progress.total) * 100}%` : '0%' }}
                        />
                    </div>
                    <p className="mt-2 text-gray-700">
                        Processed: <strong>{progress.processed}</strong> / <strong>{progress.total}</strong> | Saved: <strong>{progress.saved}</strong>
                    </p>
                </div>
            )}

            <div ref={logRef} className="w-full max-w-lg h-48 overflow-hidden border border-gray-200 rounded-xl p-4 bg-white">
                {logs.length === 0 && status === 'idle' ? (
                    <p className="text-gray-500">No logs yet.</p>
                ) : (
                    logs.map((l, i) => <p key={i} className="text-gray-800 text-sm break-words">{l}</p>)
                )}
            </div>

            {status === 'done' && <p className="mt-4 text-green-600 font-semibold">Import completed successfully!</p>}
        </div>
    );
}